import React, { useEffect, useRef, useState } from 'react';
import { Trophy, UploadCloud, CheckCircle2, AlertTriangle, Film } from 'lucide-react';
import { api } from '../lib/api';

const CHECKLIST = [
  '10–12 seconds long',
  'filmed horizontally / landscape',
  'one continuous shot',
  'steady and well lit',
  'the original-quality file',
  'free from added music or soundtrack',
  'free from added captions, logos, filters or effects',
  'suitable for public display',
  'covered by the permissions declaration below',
];

const DECLARATION = "I confirm that I am authorised to submit this video on behalf of the named business or organisation. I confirm that all necessary permissions and consents have been obtained from any identifiable people appearing in the footage, including appropriate parent or guardian consent where children are identifiable. I also confirm that I have the necessary rights and permissions for all content included in the video and that, to the best of my knowledge, its use will not infringe any third-party copyright, intellectual property, privacy, licensing or other rights. I give Leicestershire Promotions Ltd permission to edit, reproduce, display and use the submitted footage in connection with the Leicestershire Tourism & Hospitality Awards and associated Awards publicity and promotional activity.";

function fmtDeadline(iso) {
  if (!iso) return null;
  try { return new Date(iso + 'T23:59:59').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); }
  catch { return iso; }
}

export default function AwardsVideoUploadPage() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({ business_name: '', contact_name: '', email: '', phone: '', category: '' });
  const [file, setFile] = useState(null);
  const [declared, setDeclared] = useState(false);
  const [progress, setProgress] = useState(null); // null | 0-100
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/awards/video-upload/config').then((r) => {
      setConfig(r.data);
      if (r.data.categories?.[0]) setForm((f) => ({ ...f, category: r.data.categories[0] }));
    }).catch(() => setConfig({ open: true, categories: [] }));
    window.scrollTo(0, 0);
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  function pickFile(e) {
    setErr('');
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (!['mp4', 'mov', 'm4v', '3gp', '3gpp'].includes(ext)) {
      setErr('Please choose the original video file from your phone — MP4 or MOV.'); return;
    }
    if (f.size > (config?.max_bytes || 524288000)) {
      setErr('That file is over the 500MB limit. A 10–12 second clip should be well within it — please check you selected the right video.'); return;
    }
    setFile(f);
  }

  async function submit() {
    setErr('');
    if (!form.business_name.trim() || !form.contact_name.trim() || !form.email.trim() || !form.category) {
      setErr('Please complete the required fields marked *.'); return;
    }
    if (!file) { setErr('Please choose your video file.'); return; }
    if (!declared) { setErr('Please tick the permissions declaration before submitting.'); return; }
    // Retry transient failures (proxy blips, brief server restarts) — only
    // genuine 4xx validation errors stop the attempt.
    const withRetry = async (fn, tries = 4) => {
      for (let a = 1; ; a++) {
        try { return await fn(); } catch (ex) {
          const s = ex.response?.status;
          if (a >= tries || (s >= 400 && s < 500)) throw ex;
          await new Promise((r) => setTimeout(r, 2000 * a));
        }
      }
    };
    try {
      setProgress(0);
      const start = await withRetry(() => api.post('/awards/video-upload/start', {
        filename: file.name, size: file.size, content_type: file.type || 'video/mp4',
      }));
      const { upload_id, chunk_size } = start.data;
      const totalChunks = Math.ceil(file.size / chunk_size);
      for (let i = 0; i < totalChunks; i++) {
        const chunk = file.slice(i * chunk_size, (i + 1) * chunk_size);
        // Each chunk is written at its own position server-side, so a retry
        // after a network blip can never duplicate or corrupt data.
        let attempt = 0;
        for (;;) {
          try {
            await api.post(`/awards/video-upload/${upload_id}/chunk?index=${i}`, chunk, {
              headers: { 'Content-Type': 'application/octet-stream' },
            });
            break;
          } catch (ex) {
            attempt += 1;
            const status = ex.response?.status;
            if (attempt >= 4 || status === 404 || status === 413) throw ex;
            await new Promise((r) => setTimeout(r, 1500 * attempt));
          }
        }
        setProgress(Math.min(99, Math.round(((i + 1) / totalChunks) * 100)));
      }
      // /complete returns immediately; the server assembles and stores the video
      // in the background so a deploy/proxy blip can't sever the heavy step.
      const resp = await withRetry(() => api.post(`/awards/video-upload/${upload_id}/complete`, {
        business_name: form.business_name.trim(),
        contact_name: form.contact_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        category: form.category,
        declaration_accepted: true,
      }));
      if (resp.data?.status === 'assembling' || resp.data?.status === 'pending') {
        let misses = 0;
        for (let p = 0; p < 150; p++) {
          await new Promise((r) => setTimeout(r, 2500));
          try {
            const st = await api.get(`/awards/video-upload/${upload_id}/status`);
            misses = 0;
            if (st.data.status === 'received') break;
            if (st.data.status === 'failed') throw { response: { status: 400, data: { detail: st.data.error } } };
          } catch (pe) {
            if (pe.response?.status === 400) throw pe;
            misses += 1;
            if (misses >= 8) throw pe; // ~20s of consecutive failures
          }
          if (p === 149) throw { response: { status: 400, data: { detail: 'The video is taking unusually long to process — please contact us to confirm it arrived.' } } };
        }
      }
      setProgress(100);
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      const d = e.response?.data?.detail;
      const s = e.response?.status;
      if (typeof d === 'string') setErr(d);
      else if (!s || s >= 500) setErr('The server was briefly unavailable — nothing was lost. Please press Submit again to retry.');
      else setErr('The upload failed — please check your connection and try again.');
      setProgress(null);
    }
  }

  const uploading = progress !== null && !done;

  return (
    <div className="bg-cream min-h-screen">
      {/* HERO — awards branding */}
      <section className="relative bg-charcoal text-base py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 grain opacity-40"/>
        <div className="absolute -top-32 -right-32 w-[400px] h-[400px] bg-mustard/10 rounded-full blur-3xl"/>
        <div className="max-w-[760px] mx-auto px-6 relative">
          <div className="label text-mustard mb-5 inline-flex items-center gap-2"><Trophy size={14}/> 2026 · Tourism &amp; Hospitality Awards</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-6">2026 Finalist Video Upload</h1>
          <p className="text-base/85 leading-relaxed">Congratulations on becoming a finalist in the 2026 Leicestershire Tourism &amp; Hospitality Awards.</p>
          <p className="text-base/85 leading-relaxed mt-3">This year we're asking each finalist to provide a short 10–12 second video showcasing their business, venue or experience. We'll edit the clips together and use them as finalists are announced during the Awards ceremony.</p>
          <p className="text-base/70 text-sm mt-3">Please follow the filming guidance provided with your finalist information before uploading your video.</p>
        </div>
      </section>

      <div className="max-w-[760px] mx-auto px-6 py-10 md:py-14">
        {config?.deadline && (
          <div data-testid="video-deadline-banner" className="mb-8 bg-mustard/15 border border-mustard rounded-2xl px-5 py-4 font-semibold text-charcoal flex items-center gap-3">
            <Film size={18} className="text-heritage shrink-0"/> Video submission deadline: {fmtDeadline(config.deadline)}
          </div>
        )}

        {config && !config.open ? (
          <div data-testid="video-upload-closed" className="bg-white border border-charcoal/10 rounded-3xl p-8 text-center">
            <h2 className="font-display text-2xl mb-3">Video submissions have now closed</h2>
            <p className="text-muted">If you still need to send us your finalist video, please contact us at <a className="text-heritage underline" href="mailto:info@goleicestershire.com">info@goleicestershire.com</a> and we'll help.</p>
          </div>
        ) : done ? (
          <div data-testid="video-upload-success" className="bg-white border border-forest/30 rounded-3xl p-8 md:p-10 text-center">
            <CheckCircle2 size={48} className="text-forest mx-auto mb-4"/>
            <h2 className="font-display text-3xl mb-4">Thank you – we've received your finalist video.</h2>
            <p className="text-muted leading-relaxed">Your video has been successfully submitted for the 2026 Leicestershire Tourism &amp; Hospitality Awards.</p>
            <p className="text-muted leading-relaxed mt-3">If we experience any problems with the file or need anything further, we'll contact you using the details provided.</p>
            <p className="text-muted leading-relaxed mt-3 font-semibold">There is no need to submit it again.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* GUIDANCE */}
            <div className="bg-white border border-charcoal/10 rounded-3xl p-6 md:p-8">
              <h2 className="font-display text-xl mb-4">Before uploading, please check that your video is:</h2>
              <ul className="space-y-2" data-testid="video-guidance-checklist">
                {CHECKLIST.map((c) => (
                  <li key={c} className="flex items-start gap-2.5 text-sm text-charcoal/80">
                    <CheckCircle2 size={16} className="text-forest mt-0.5 shrink-0"/> {c}
                  </li>
                ))}
              </ul>
              <div className="flex items-end gap-6 mt-6" data-testid="video-orientation-visual">
                <div className="text-center">
                  <div className="w-28 h-16 rounded-lg border-2 border-forest bg-forest/10 flex items-center justify-center text-forest font-bold text-xl">✓</div>
                  <div className="text-xs font-semibold mt-1.5 text-forest tracking-wide">LANDSCAPE</div>
                </div>
                <div className="text-center">
                  <div className="w-16 h-24 rounded-lg border-2 border-heritage bg-heritage/10 flex items-center justify-center text-heritage font-bold text-xl">✕</div>
                  <div className="text-xs font-semibold mt-1.5 text-heritage tracking-wide">PORTRAIT</div>
                </div>
              </div>
            </div>

            {/* FORM */}
            <div className="bg-white border border-charcoal/10 rounded-3xl p-6 md:p-8 space-y-5">
              <h2 className="font-display text-xl">Your details</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="text-sm font-medium sm:col-span-2">Business / venue name *
                  <input value={form.business_name} onChange={(e) => set('business_name', e.target.value)} disabled={uploading}
                    data-testid="video-business-input" className="mt-1.5 w-full border border-charcoal/15 rounded-xl px-4 py-3 bg-cream/50"/>
                </label>
                <label className="text-sm font-medium">Contact name *
                  <input value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} disabled={uploading}
                    data-testid="video-contact-input" className="mt-1.5 w-full border border-charcoal/15 rounded-xl px-4 py-3 bg-cream/50"/>
                </label>
                <label className="text-sm font-medium">Email address *
                  <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} disabled={uploading}
                    data-testid="video-email-input" className="mt-1.5 w-full border border-charcoal/15 rounded-xl px-4 py-3 bg-cream/50"/>
                </label>
                <label className="text-sm font-medium">Telephone number
                  <input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} disabled={uploading}
                    data-testid="video-phone-input" className="mt-1.5 w-full border border-charcoal/15 rounded-xl px-4 py-3 bg-cream/50"/>
                </label>
                <label className="text-sm font-medium">Awards category *
                  {config?.categories?.length ? (
                    <select value={form.category} onChange={(e) => set('category', e.target.value)} disabled={uploading}
                      data-testid="video-category-select" className="mt-1.5 w-full border border-charcoal/15 rounded-xl px-4 py-3 bg-cream/50">
                      {config.categories.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  ) : (
                    <input value={form.category} onChange={(e) => set('category', e.target.value)} disabled={uploading}
                      data-testid="video-category-input" placeholder="Your awards category"
                      className="mt-1.5 w-full border border-charcoal/15 rounded-xl px-4 py-3 bg-cream/50"/>
                  )}
                </label>
              </div>

              {/* UPLOAD */}
              <div className="pt-2">
                <div className="text-sm font-medium mb-1.5">Video file *</div>
                <div className="flex items-start gap-2.5 bg-mustard/10 border border-mustard/40 rounded-xl px-4 py-3 mb-3 text-sm text-charcoal/80" data-testid="video-audio-reminder">
                  <AlertTriangle size={16} className="text-heritage mt-0.5 shrink-0"/>
                  <span>Please upload your original video <strong>without added music, soundtrack, voiceover, captions, graphics or filters</strong>. Incidental background audio will be removed by us during editing.</span>
                </div>
                <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/*,.mp4,.mov,.m4v" onChange={pickFile}
                  className="hidden" data-testid="video-file-input" disabled={uploading}/>
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                  data-testid="video-choose-file-btn"
                  className="w-full border-2 border-dashed border-charcoal/20 rounded-2xl px-5 py-8 text-center hover:border-heritage transition-colors">
                  <UploadCloud size={28} className="mx-auto text-heritage mb-2"/>
                  {file ? (
                    <span className="font-semibold" data-testid="video-file-chosen">{file.name} <span className="text-charcoal/50 font-normal">({(file.size / (1024 * 1024)).toFixed(1)}MB)</span></span>
                  ) : (
                    <span className="text-charcoal/70">Tap to choose your video from your phone or computer<br/><span className="text-xs text-charcoal/50">MP4 or MOV · up to 500MB · original quality — we never recompress your file</span></span>
                  )}
                </button>
              </div>

              {/* DECLARATION */}
              <label className="flex items-start gap-3 pt-2 cursor-pointer">
                <input type="checkbox" checked={declared} onChange={(e) => setDeclared(e.target.checked)} disabled={uploading}
                  data-testid="video-declaration-checkbox" className="mt-1 w-4 h-4 shrink-0"/>
                <span className="text-xs text-charcoal/75 leading-relaxed">{DECLARATION}</span>
              </label>
              <p className="text-[11px] text-charcoal/50 leading-relaxed pl-7">Please retain your own records of any permissions or consents obtained. You do not need to upload these unless we subsequently ask you to provide evidence of them.</p>

              {err && <div data-testid="video-upload-error" className="p-3 rounded-xl bg-heritage/10 text-heritage text-sm">{err}</div>}

              {uploading ? (
                <div data-testid="video-upload-progress">
                  <div className="h-3 bg-stone rounded-full overflow-hidden">
                    <div className="h-full bg-heritage transition-all" style={{ width: `${progress}%` }}/>
                  </div>
                  <div className="text-sm text-charcoal/60 mt-2 text-center">{progress < 99 ? `Uploading… ${progress}%` : 'Finalising your submission…'} Please keep this page open.</div>
                </div>
              ) : (
                <button onClick={submit} disabled={!declared || !file}
                  data-testid="video-submit-btn"
                  className="w-full sm:w-auto px-8 py-4 rounded-full bg-heritage text-cream font-semibold text-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-charcoal transition-colors">
                  Submit my finalist video
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
